import React, { useState, useEffect, useCallback } from 'react';
import tyroConfigService from '../../services/tyroConfigService';
import storeService from '../../services/storeService';
import { useStore } from '../../context/StoreContext';
import { useAuth } from '../../context/AuthContext';
import { loadTyroSdk } from '../../utils/tyroLoader';
import { getTyroClient, resetTyroClient } from '../../utils/tyroClient';

const sanitizeIntegrationKeyMessage = (message) => {
  if (!message) return '';
  return message.replace(/Integration Key:?[\s:=]*[0-9a-f]+/gi, 'Integration key captured securely.');
};

const formatPairingStatusMessage = (response) => {
  if (!response) return '';
  const sanitizedMessage = sanitizeIntegrationKeyMessage(response.message);
  if (response.status === 'success') {
    return 'Pairing successful. Integration key captured securely.';
  }
  if (response.status === 'failure') {
    return sanitizedMessage ? `Pairing failed: ${sanitizedMessage}` : 'Pairing failed.';
  }
  return sanitizedMessage || 'Pairing update received.';
};

const TyroSettings = () => {
  const [formData, setFormData] = useState({ mid: '', tid: '', integratedReceiptEnabled: true, useHeadlessMode: true });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showLogs, setShowLogs] = useState(false);
  const [pairingStatus, setPairingStatus] = useState('');
  const { activeStore } = useStore();
  const { tillId, getCurrentTillForStore, user } = useAuth();
  const currentTillId = activeStore?._id ? getCurrentTillForStore(activeStore._id) : tillId;
  const [tillTid, setTillTid] = useState('');
  const [tillName, setTillName] = useState('');

  const fetchTyroConfig = useCallback(async () => {
    if (!activeStore) {
      setIsLoading(false);
      return;
    }
    try {
      const data = await tyroConfigService.getTyroConfig(activeStore._id, { includeSensitive: true });
      if (data) {
        setFormData({
          mid: data.mid || '',
          tid: data.tid || '',
          integratedReceiptEnabled: data.integratedReceiptEnabled !== false,
          useHeadlessMode: data.useHeadlessMode !== false,
          apiKey: data.apiKey || '',
        });
      }
    } catch (err) {
      setError('Failed to fetch Tyro configuration.');
    } finally {
      setIsLoading(false);
    }
  }, [activeStore]);

  useEffect(() => {
    fetchTyroConfig();
  }, [fetchTyroConfig]);

  const updateTillPairingStatus = useCallback(
    async (status, meta = {}) => {
      if (!activeStore || !currentTillId) return;
      try {
        await storeService.updateTill(activeStore._id, currentTillId, {
          tyroPairingStatus: status,
          ...meta,
        });
      } catch (err) {
        console.warn('Failed to update till pairing status', err);
      }
    },
    [activeStore, currentTillId]
  );

  useEffect(() => {
    const fetchTillDetails = async () => {
      if (!activeStore || !currentTillId) {
        setTillTid('');
        setTillName('');
        return;
      }
      try {
        const storeDetails = await storeService.getStoreById(activeStore._id);
        const matchingTill = storeDetails?.tills?.find((till) => till._id === currentTillId);
        setTillTid(matchingTill?.tid || '');
        setTillName(matchingTill?.name || '');
      } catch (err) {
        console.warn('Failed to load till details for Tyro pairing', err);
        setTillTid('');
        setTillName('');
      }
    };

    fetchTillDetails();
  }, [activeStore, currentTillId]);

  useEffect(() => {
    loadTyroSdk().catch((err) => {
      console.error('Failed to load Tyro SDK', err);
      setPairingStatus('Unable to load Tyro library. Please check network connectivity.');
    });
  }, []);

  const handlePairing = async () => {
    if (!window.TYRO) {
      try {
        await loadTyroSdk();
      } catch (err) {
        setPairingStatus('Tyro library not loaded. Please try again in a moment.');
        return;
      }
    }

    if (!currentTillId || !tillTid) {
      setPairingStatus('Set a terminal (Till) with a TID in Store Settings → Devices before pairing.');
      return;
    }

    updateTillPairingStatus('pending', { tyroPairingError: null });
    setPairingStatus('Initiating pairing...');
    const siteReference =
      [activeStore?.address?.street, activeStore?.address?.suburb, activeStore?.address?.state]
        .filter(Boolean)
        .join(', ') ||
      activeStore?.name ||
      '';
    const posProductInfo = {
      posProductVendor: 'Bizessence Pty Ltd',
      posProductName: 'BoltinPOS',
      posProductVersion: '1.0.0',
      siteReference,
    };

    let iclient;
    try {
      iclient = await getTyroClient({
        apiKey: formData.apiKey,
        posProductInfo,
        preferHeadless: false,
      });
    } catch (clientError) {
      console.error('Failed to initialize Tyro client', clientError);
      setPairingStatus(clientError.message || 'Unable to initialize Tyro client.');
      return;
    }

    if (typeof iclient.pairTerminal !== 'function') {
      setPairingStatus('Tyro client in this session does not support pairing. Refresh or open Settings in a new window.');
      return;
    }

    iclient.pairTerminal(formData.mid, tillTid, (response) => {
      const statusMessage = formatPairingStatusMessage(response);
      if (statusMessage) {
        setPairingStatus(statusMessage);
      }
      if (response.status === 'success' && response.integrationKey) {
        updateTillPairingStatus('paired', {
          tyroPairedAt: new Date().toISOString(),
          tyroPairedBy: user?._id || undefined,
          tyroPairingError: null,
          tyroIntegrationKey: response.integrationKey,
        });
        setSuccess('Integration key captured and saved to this terminal.');
        setTimeout(() => setSuccess(''), 3000);
      } else if (response.status === 'failure') {
        updateTillPairingStatus('failed', {
          tyroPairingError: response.message || 'Pairing failed',
        });
      }
    });
  };

  const handleResetTyroSession = () => {
    resetTyroClient();
    setPairingStatus('Tyro session reset. You can retry pairing now.');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async () => {
    if (!activeStore) {
      setError('No store selected.');
      return;
    }
    setError('');
    setSuccess('');
    try {
      const payload = {
        mid: formData.mid,
        integratedReceiptEnabled: formData.integratedReceiptEnabled,
        useHeadlessMode: formData.useHeadlessMode,
      };
      await tyroConfigService.updateTyroConfig(activeStore._id, payload);
      setSuccess('Tyro configuration saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save Tyro configuration.');
    }
  };

  if (isLoading) return <div>Loading Tyro settings...</div>;

  if (!activeStore) {
    return <div className="text-center text-gray-500">Please select a store to configure Tyro settings.</div>;
  }

  const tyroLogsUrl =
    process.env.REACT_APP_TYRO_LOGS_URL ||
    'https://iclientsimulator.test.tyro.com/logs.html';

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Tyro Settings</h2>
      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
      {success && <p className="bg-green-100 text-green-700 p-3 rounded mb-4">{success}</p>}

      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Merchant ID (MID)</label>
            <input
              name="mid"
              value={formData.mid}
              onChange={handleChange}
              className="mt-1 p-2 border rounded-md w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Terminal ID (TID)</label>
            <input
              name="tid"
              value={tillTid || 'Not set'}
              readOnly
              className="mt-1 p-2 border rounded-md w-full bg-gray-50 text-gray-600"
            />
            <p className="text-xs text-gray-500 mt-1">
              {tillTid
                ? `Using TID from terminal setup${tillName ? ` (${tillName})` : ''}.`
                : 'Select a terminal and set its TID in Store Settings → Devices.'}
            </p>
          </div>
          <div className="flex items-center justify-between border rounded-md px-4 py-3 bg-gray-50">
            <div>
              <p className="text-sm font-medium text-gray-700">Integrated receipt printing</p>
              <p className="text-xs text-gray-500">Enable to print Tyro receipts on this POS.</p>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="integratedReceiptEnabled"
                checked={formData.integratedReceiptEnabled}
                onChange={handleChange}
                className="sr-only"
              />
              <span
                className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors duration-200 ${
                  formData.integratedReceiptEnabled ? 'bg-primary' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-200 ${
                    formData.integratedReceiptEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </span>
            </label>
          </div>
        </div>
        <div className="flex justify-end pt-4">
          <button onClick={handleSave} className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark">
            Save Changes
          </button>
        </div>
      </div>

      <div className="mt-8">
        <h3 className="text-lg font-bold mb-2">Pair Terminal</h3>
        <p className="text-sm text-gray-600 mb-4">
          Click the button below to pair your Tyro terminal. Make sure you have saved your MID and TID above.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handlePairing}
            disabled={!currentTillId || !tillTid}
            className="px-4 py-2 text-sm font-semibold border border-primary text-primary rounded-md hover:bg-primary hover:text-white"
          >
            Pair Terminal
          </button>
          <button
            type="button"
            onClick={handleResetTyroSession}
            className="px-4 py-2 text-sm font-semibold border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100"
          >
            Reset Tyro Session
          </button>
        </div>
        {pairingStatus && <p className="text-sm text-gray-600 mt-2">{pairingStatus}</p>}
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-lg font-bold">Tyro iClient Logs</h3>
            <p className="text-sm text-gray-600">Review recent Tyro simulator logs without leaving the POS.</p>
          </div>
          <div className="space-x-2">
            <a
              href={tyroLogsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center px-4 py-2 text-sm font-semibold text-primary border border-primary rounded-md hover:bg-primary hover:text-white"
            >
              Open in new tab
            </a>
            <button
              type="button"
              onClick={() => setShowLogs(prev => !prev)}
              className="inline-flex items-center px-4 py-2 text-sm font-semibold bg-gray-800 text-white rounded-md hover:bg-gray-900"
            >
              {showLogs ? 'Hide' : 'Show'} inline
            </button>
          </div>
        </div>
        {showLogs && (
          <iframe src={tyroLogsUrl} title="Tyro iClient Logs" className="w-full h-96 border rounded-md" />
        )}
      </div>
    </div>
  );
};

export default TyroSettings;
